import { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { revealUp, EASE_OUT } from '../lib/anim';
import { PROJECTS_DATA } from "../data/portfolio";


function ProjectCard({ project, i }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: '50%', y: '50%' });
  const [hover, setHover] = useState(false);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    setPos({
      x: `${((e.clientX - rect.left) / rect.width) * 100}%`,
      y: `${((e.clientY - rect.top) / rect.height) * 100}%`,
    });
  };




  const wide = i % 3 === 0;

  return (
    <motion.article
      variants={revealUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.25 }}
      className={`group ${wide ? 'md:col-span-12' : 'md:col-span-6'}`}
    >
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={`relative overflow-hidden rounded-[3px] border border-border ${
          wide ? 'aspect-[16/9]' : 'aspect-[4/3]'
        }`}
      >
        {project.type === 'photo' ? (
          <>
            <motion.img
              style={{ y: imgY, scale: 1.12 }}
              src={project.image}
              alt={project.title}
              className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:grayscale-0"
            />
            <div className="absolute inset-0 bg-background/45 transition-opacity duration-500 group-hover:opacity-20" />
            {/* Spotlight */}
            <div
              className="pointer-events-none absolute inset-0 transition-opacity duration-300"
              style={{
                opacity: hover ? 1 : 0,
                background: `radial-gradient(360px circle at ${pos.x} ${pos.y}, rgba(255,255,255,0.16), transparent 65%)`,
              }}
            />
          </>
        ) : (
          <div
            className="relative h-full w-full"
            style={{ background: project.gradient }}
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_120%_at_20%_0%,rgba(255,255,255,0.08),transparent_55%)]" />
            <span className="absolute inset-0 flex items-center justify-center font-display text-[22vw] font-extrabold uppercase tracking-tighter text-white/[0.04] md:text-[12vw]">
              {project.title}
            </span>
            <div
              className="pointer-events-none absolute inset-0 transition-opacity duration-300"
              style={{
                opacity: hover ? 1 : 0,
                background: `radial-gradient(360px circle at ${pos.x} ${pos.y}, rgba(255,255,255,0.12), transparent 65%)`,
              }}
            />
          </div>
        )}

        {/* Top meta */}
        <div className="absolute left-5 top-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground/90">
          <span>{project.index}</span>
          <span className="h-px w-6 bg-foreground/40" />
          <span>{project.year}</span>
        </div>

        {/* Hover CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: hover ? 1 : 0, scale: hover ? 1 : 0.8 }}
          transition={{ duration: 0.35, ease: EASE_OUT }}
          className="absolute right-5 top-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground"
          data-testid={`project-cta-${project.id}`}        >
          <ArrowUpRight className="h-5 w-5" />
            </motion.div>
      </div>

      <div className="mt-5 flex flex-col justify-between gap-3 md:flex-row md:items-start">
        <div>
          <h3 className="font-display text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
            {project.title}
          </h3>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
            {project.category}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 md:max-w-xs md:justify-end">
          {project.tags.map((t) => (
            <span
              key={t}
              className="rounded-full border border-border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        {project.description}
      </p>
    </motion.article>
  );
}

export default function Projects() {
  return (
    <section
      id="work"
      data-testid="projects-section"
       className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32"
    >
      <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            (Selected Work)
          </span>
          <h2 className="mt-4 font-display text-5xl font-extrabold leading-[0.9] tracking-tighter text-foreground md:text-7xl">
            Case
            <br />
            Studies
          </h2>
        </div>
        <p className="max-w-sm text-base leading-relaxed text-muted-foreground">
          A selection of products I&apos;ve shaped end-to-end — from strategy and
          systems to the last frame of motion.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-12">
        {PROJECTS_DATA.map((p, i) => (
          <ProjectCard key={p.id} project={p} i={i} />
        ))}
      </div>
    </section>
  );
}
